<template>
    <div class="image-generator">
        <!-- Generator Interface -->
        <div class="generator-interface">
            <!-- Navigation Tabs -->
            <div class="generator-tabs">
                <div
                    v-for="(tab, index) in tabs"
                    :key="index"
                    class="tab-item"
                    :class="{ active: activeTab === index }"
                    @click="setActiveTab(index)"
                >
                    <div class="tab-icon">
                        <svg
                            v-if="index === 0"
                            class="w-5 h-5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                        >
                            <path
                                fill-rule="evenodd"
                                d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                                clip-rule="evenodd"
                            />
                        </svg>
                        <img
                            src="~/assets/images/videoImgPages/imgToConverse.png"
                            :alt="t('menus.createModal.imageToImage')"
                            v-else
                        />
                        <!-- <svg v-else class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clip-rule="evenodd" />
                        </svg> -->
                    </div>
                    {{ tab?.name }}
                    <svg
                        v-if="tab?.external"
                        class="w-4 h-4 ml-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                            clip-rule="evenodd"
                        />
                    </svg>
                </div>
            </div>

            <!-- Header Section with Title and Model Selector -->

            <div class="model-selector-wrapper mt-6">
                <tool-model-select
                    v-model:model="selectedModelKey"
                    :model_list="currentModelList"
                    :type="currentModelType"
                ></tool-model-select>
            </div>

            <!-- Tab Content -->
            <div class="tab-content">
                <!-- Text to Image Tab -->
                <div v-if="activeTab === 0" class="tab-panel">
                    <div class="input-section">
                        <div class="input-header">
                            <label class="input-label">{{
                                t('menus.prompt')
                            }}</label>
                        </div>
                        <textarea
                            v-model="promptText"
                            class="prompt-textarea !resize-none"
                            :placeholder="t('menus.promptPlaceholder')"
                            rows="4"
                            maxlength="2000"
                            @input="updateCharCount"
                        ></textarea>
                        <div class="textarea-footer">
                            <div class="char-count">{{ charCount }} / 2000</div>
                        </div>
                    </div>
                </div>

                <!-- Image to Image Tab -->
                <div v-if="activeTab === 1" class="tab-panel">
                    <div class="input-section">
                        <label class="input-label">{{
                            t('menus.createModal.image')
                        }}</label>
                        <ModelPageImageUpload
                            :max-size-m-b="10"
                            :max-resolution="4096"
                            :allow-multiple="false"
                            accept-types="image/jpeg,image/png,image/webp"
                            @upload-success="onImageUploadSuccess"
                            @upload-error="onImageUploadError"
                            @files-change="onImageFilesChange"
                        />
                    </div>
                </div>
            </div>

            <!-- Generate Button -->
            <div class="generate-section">
                <button
                    class="generate-button"
                    :disabled="!canGenerate"
                    @click="generateImage($event)"
                >
                    <img
                        src="~/assets/images/common/create_btn.svg"
                        alt="Create icon"
                        aria-hidden="true"
                        role="presentation"
                        class="w-6 h-6"
                    />
                    {{ t('menus.create') }}
                </button>
            </div>
            <div
                v-if="!isLoggedIn && LOGIN_ENTRY_ENABLED"
                class="w-full justify-center flex items-center"
            >
                <img
                    class="w-5 h-5"
                    src="https://static.weryai.com/growth/image/weryai_ce29b49010526e7fa8e91d971a726de4.webp"
                    alt="Gift icon"
                    aria-hidden="true"
                    role="presentation"
                />
                <span class="text-xs text-white ml-[5px] mr-[10px]">{{
                    t('common.loginToGetFree')
                }}</span>
                <div
                    class="flex items-center text-sm font-semibold text-text-active cursor-pointer hover:underline hover:scale-110 transition-all duration-300"
                    @click="handleTryNow"
                >
                    <span class="mr-[5px]">{{ t('common.tryNow') }}</span>
                    <img
                        class="w-3 h-3"
                        src="https://static.weryai.com/growth/image/weryai_404732b1381449b983bc6509b6b7ce3d.webp"
                        alt="Arrow icon"
                        aria-hidden="true"
                        role="presentation"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import ModelPageImageUpload from '~/components/ModelPageImageUpload.vue'
import ToolModelSelect from '~/components/Tool/ModelSelect.vue'
import { analyticsEvent } from '~/utils/log-event-func'
import { useI18n } from '~/composables/useI18n'
import { LOGIN_ENTRY_ENABLED } from '~/utils/feature-flags'
import { useToolsHooks } from '@/utils/useHooks'
import { useAppStore } from '~/stores/app'
import { formatAndGroupData } from '~/utils/common'
import { showLoginModal } from '~/utils/emitter'
import { useUserStore } from '~/stores/user'
const userStore = useUserStore()
const isLoggedIn = computed(() => userStore.isLoggedIn)
const { t } = useI18n()
const { handleMenuLinkClick } = useToolsHooks()
const appStore = useAppStore()
const modelData = computed(() => appStore.modelData)

// 页面名称与实际模型名称的映射关系
const MODEL_NAME_MAPPING: Record<string, string> = {
    'Google Nano Banana': 'Nano-banana',
    FLUX: 'Flux',
    'Flux Kontext': 'Flux',
    'Google Imagen 4': 'Imagen4',
    Imagen: 'Imagen4',
    'GPT-4o': 'Gpt Image Mini',
    'Qwen Image': 'Qwen Image',
    'Wan 2.5': 'Wan2.5',
    'Wan AI': 'Wan2.5',
    'Seedream 4.0': 'Seedream 4.0',
    Seedream: 'Seedream 4.0',
    Dreamina: 'Dreamina 4.0',
    'Dreamina 4.0': 'Dreamina 4.0',
}

// 类型定义
interface UploadedFile {
    id: string
    file: File
    name: string
    size: number
    previewUrl: string
    uploadStatus: 'pending' | 'uploading' | 'success' | 'error'
    uploadResult?: unknown
    errorMessage?: string
}

interface UploadResult {
    data?: {
        url?: string
        object_url_list?: string[]
    }
    url?: string
}

// Props
interface Props {
    modelName?: string
    defaultTab?: number
    modelKey?: string
}

const props = withDefaults(defineProps<Props>(), {
    modelName: 'Google Nano Banana',
    defaultTab: 1,
    modelKey: 'GEMINI_2_5_FLASH',
})

// Emits
const emit = defineEmits<{
    generate: [
        data: {
            prompt: string
            image?: File
            tabType: 'text-to-image' | 'image-to-image'
        },
    ]
}>()

// 当前模型类型
const currentModelType = computed(() => {
    // activeTab: 0 = 文生图, 1 = 图生图
    return activeTab.value === 0 ? 'text_to_image' : 'image_to_image'
})

// 当前模型列表（只显示父模型，不显示版本）
const currentModelList = computed(() => {
    if (!modelData.value) return []
    const imageModels =
        modelData.value?.image_models?.[currentModelType.value] || []
    // 使用 formatAndGroupData 处理模型列表，只返回父模型
    return formatAndGroupData(imageModels)
})

// 选中的模型key
const selectedModelKey = ref('')

// Reactive data
const activeTab = ref(props.defaultTab)
const promptText = ref('')
const uploadedImage = ref<File | null>(null)
const charCount = ref(0)

// Computed
const tabs = computed(() => {
    const allTabs = [
        { name: t('menus.createModal.textToImage'), external: false },
        { name: t('menus.createModal.imageToImage'), external: false },
    ]

    // Google Imagen 4 只支持文生图，不支持图生图
    if (props.modelName === 'Google Imagen 4') {
        activeTab.value = 0
        return [allTabs[0]] // 只返回文生图 tab
    }

    return allTabs
})

const canGenerate = computed(() => {
    if (activeTab.value === 0) {
        // Text to Image: only need prompt
        return promptText.value.trim().length > 0
    } else {
        // Image to Image: only need image
        return uploadedImage.value !== null
    }
})

// Methods
const setActiveTab = (index: number) => {
    activeTab.value = index
}

const getCurrentTabTitle = () => {
    return tabs.value[activeTab.value]?.name || ''
}

const updateCharCount = () => {
    charCount.value = promptText.value.length
}

// Watch模型列表变化，根据 modelKey 或 modelName prop 自动选择模型
watch(
    [() => currentModelList.value, () => activeTab.value],
    ([newModelList]: [any[], number]) => {
        if (newModelList && newModelList.length > 0) {
            // 如果还没有选中模型，或者当前选中的模型不在列表中
            if (
                !selectedModelKey.value ||
                !newModelList.find(
                    (m: any) => m.model_key === selectedModelKey.value
                )
            ) {
                let matchedModel = null

                // 优先使用 modelKey prop 进行精确匹配
                if (props.modelKey) {
                    matchedModel = newModelList.find(
                        (m: any) => m.model_key === props.modelKey
                    )
                }

                // 如果 modelKey 没有匹配，尝试根据 modelName prop 找到对应的模型
                if (!matchedModel && props.modelName) {
                    // 先查找映射表，获取实际的模型名称
                    const actualModelName =
                        MODEL_NAME_MAPPING[props.modelName] || props.modelName
                    const modelNameLower = actualModelName.toLowerCase().trim()

                    // 第一步：尝试精确匹配（title 或 model_key）
                    matchedModel = newModelList.find((m: any) => {
                        const titleLower = (m.title || '').toLowerCase().trim()
                        const keyLower = (m.model_key || '')
                            .toLowerCase()
                            .trim()
                        return (
                            titleLower === modelNameLower ||
                            keyLower === modelNameLower
                        )
                    })

                    // 第二步：如果精确匹配失败，尝试模糊匹配
                    if (!matchedModel) {
                        matchedModel = newModelList.find((m: any) => {
                            const titleLower = (m.title || '')
                                .toLowerCase()
                                .trim()
                            const keyLower = (m.model_key || '')
                                .toLowerCase()
                                .trim()

                            // 检查 modelName 是否是 title 或 key 的开头部分或包含关系
                            return (
                                titleLower.startsWith(modelNameLower) ||
                                keyLower.startsWith(modelNameLower) ||
                                titleLower.includes(' ' + modelNameLower) ||
                                titleLower.includes(
                                    actualModelName.toLowerCase()
                                ) ||
                                actualModelName
                                    .toLowerCase()
                                    .includes(titleLower)
                            )
                        })
                    }
                }

                // 如果找到匹配的模型，使用它；否则使用第一个模型
                selectedModelKey.value =
                    matchedModel?.model_key || newModelList[0]?.model_key || ''
            }
        }
    },
    {
        immediate: true,
        flush: 'post',
    }
)

// ModelPageImageUpload 事件处理
const onImageUploadSuccess = (files: UploadedFile[]) => {
    if (files.length > 0 && files[0]) {
        uploadedImage.value = files[0].file

        // 获取上传后的图片地址
        const uploadResult = files[0].uploadResult as UploadResult
        const imageUrl =
            uploadResult?.data?.object_url_list?.[0] ||
            uploadResult?.data?.url ||
            uploadResult?.url

        if (imageUrl && is_client) {
            // 写入 localStorage
            localStorage.setItem('modelPageFileUrl', imageUrl)
            if (selectedModelKey.value) {
                localStorage.setItem(
                    'modelPageModelKey',
                    selectedModelKey.value
                )
            }

            // 埋点：点击 Nano Banana 下一步（图生图）
            analyticsEvent('click_nano_next', {
                tab_type: 'image-to-image',
            })
            analyticsEvent('click_model_page_common_create', {
                model_name: selectedModelKey.value || props.modelKey || '',
                type: 'image',
            })

            // 短暂延迟以确保数据保存完成，然后跳转
            setTimeout(() => {
                const encodedImageUrl = encodeURIComponent(imageUrl)
                handleMenuLinkClick(
                    `/image-to-image?defaultModel=${props.modelKey}&customType=modelPage&imageUrl=${encodedImageUrl}`
                )
            }, 100)
            // 跳转到图生图页面
        }
    }
}

const onImageUploadError = (error: string) => {
    console.error('图片上传失败:', error)
}

const onImageFilesChange = (files: UploadedFile[]) => {
    if (files.length === 0) {
        uploadedImage.value = null
    } else if (files[0]) {
        uploadedImage.value = files[0].file
    }
}

// 初始化时获取模型数据
onMounted(() => {
    if (!modelData.value) {
        appStore.getModelList()
    }
})

const is_client = import.meta.client
const generateImage = (event: MouseEvent) => {
    if (!canGenerate.value) return

    // 文生图 Tab：保存提示词并跳转
    if (activeTab.value === 0) {
        const prompt = promptText.value.trim()
        if (prompt && is_client) {
            // 保存提示词到 localStorage
            localStorage.setItem('modelPagePrompt', prompt)
            if (selectedModelKey.value) {
                localStorage.setItem(
                    'modelPageModelKey',
                    selectedModelKey.value
                )
            }

            // 埋点：点击 Nano Banana 下一步（文生图）
            analyticsEvent('click_nano_next', {
                tab_type: 'text-to-image',
            })
            analyticsEvent('click_model_page_common_create', {
                model_name: selectedModelKey.value || props.modelKey || '',
                type: 'image',
            })

            // 短暂延迟以确保数据保存完成，然后跳转
            setTimeout(() => {
                const encodedPrompt = encodeURIComponent(prompt)
                const selectedModelParam = selectedModelKey.value
                    ? `&selectedModel=${selectedModelKey.value}`
                    : ''
                handleMenuLinkClick(
                    `/text-to-image?defaultModel=${props.modelKey}&customType=modelPage&prompt=${encodedPrompt}${selectedModelParam}`,
                    event
                )
            }, 100)
        }
        return
    }

    // 图生图 Tab：直接跳转，不生成
    if (activeTab.value === 1) {
        // 图生图功能已经在 onImageUploadSuccess 中处理跳转
        return
    }

    // 文生图 Tab 的原有逻辑（这里不会执行，因为已经在前面 return 了）
    const tabType = activeTab.value === 0 ? 'text-to-image' : 'image-to-image'

    emit('generate', {
        prompt: promptText.value.trim(),
        image: uploadedImage.value || undefined,
        tabType,
    })
}

const handleTryNow = () => {
    showLoginModal()
}
</script>

<style lang="less" scoped>
.image-generator {
    width: 100%;
    max-width: 800px;
    margin: 0 auto;
}

.generator-interface {
    border-radius: 1rem;
    padding: 2rem;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.1);
    background-color: #22242d;
}

.generator-tabs {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    padding-bottom: 1rem;

    img {
        width: 1.25rem;
        height: 1.25rem;
    }
}

.tab-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0rem 1.5rem;
    border-radius: 0.5rem;
    cursor: pointer;
    transition: all 0.3s ease;
    /* color: rgba(255, 255, 255, 0.7); */
    color: #adadad;
    font-weight: 500;
    position: relative;

    &:hover {
        color: rgba(255, 255, 255, 0.9);
    }

    &.active {
        color: white;

        &::after {
            content: '';
            position: absolute;
            bottom: -1rem;
            left: 0;
            right: 0;
            height: 3px;
            /* background: linear-gradient(90deg, #ec4899, #be185d); */
            background-color: #fff;
            border-radius: 1px;
        }
    }
}

.tab-icon {
    display: flex;
    align-items: center;
    justify-content: center;
}

.header-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
}

.section-title {
    font-size: 1.125rem;
    font-weight: 500;
    color: white;
    margin: 0;
}

.model-selector-wrapper {
    flex: 1;
    max-width: 400px;
}

.tab-content {
    margin-bottom: 2rem;
}

.tab-panel {
    animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.input-section {
    margin-top: 1.5rem;
    margin-bottom: 1.5rem;
}

.input-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.input-label {
    color: white;
    font-weight: 400;
    font-size: 14px;
    margin-bottom: 10px;
    display: inline-block;
}

.prompt-textarea {
    width: 100%;
    padding: 1rem;
    border-radius: 0.5rem;
    color: white;
    font-size: 0.875rem;
    line-height: 1.5;
    resize: vertical;
    min-height: 12rem;
    transition: all 0.3s ease;
    background-color: #2c2e36;
    border: unset;
    &::placeholder {
        color: #999;
    }

    &:focus {
        outline: none;
        --tw-ring-color: rgba(255, 255, 255, 0.3);
    }
}

.textarea-footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-top: 0.5rem;
}

.char-count {
    color: rgba(255, 255, 255, 0.5);
    font-size: 0.75rem;
}

.prompt-section {
    margin-top: 1.5rem;
}

.generate-section {
    display: flex;
    justify-content: center;
    padding: 1rem 1.5rem 1.5rem;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.generate-button {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem 2rem;
    color: black;
    border: none;
    border-radius: 0.75rem;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
    background-color: var(--weryai-text-active);
    width: 18rem;
    height: 3.25rem;
    justify-content: center;

    &::before {
        content: '';
        position: absolute;
        inset: -2px;
        border-radius: 0.75rem;
        z-index: -1;
        opacity: 0.3;
        filter: blur(8px);
    }

    &:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 12px 40px rgba(153, 247, 104, 0.5);
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        transform: none;
    }

    &:active:not(:disabled) {
        transform: translateY(0);
    }
    img {
        width: 1.5rem;
        height: 1.5rem;
    }
}

/* Responsive Design */
@media (max-width: 768px) {
    .generator-interface {
        padding: 1.5rem;
    }

    .generator-tabs {
        flex-direction: column;
        gap: 0.25rem;
    }

    .tab-item {
        justify-content: center;
    }

    .model-selector {
        justify-content: center;
    }

    .input-header {
        flex-direction: column;
        gap: 0.75rem;
        align-items: flex-start;
    }

    .textarea-footer {
        flex-direction: column;
        gap: 0.5rem;
        align-items: flex-end;
    }

    .generate-button {
        width: 100%;
        justify-content: center;
    }
}
</style>
